/**
 * The wire convention for the coordinator, plus both ends of it:
 * - `createRpcHandler(coordinator)`: a fetch handler that serves any {@link Coordinator} over HTTP.
 * - `createHttpCoordinator({ baseUrl })`: a {@link Coordinator} that talks to one over HTTP and WebSocket.
 *
 * Convention: `POST /v1/rpc/<operation>` with the operation's input as the JSON body. The response body is
 * always an {@link RpcResponse}: `{ ok: true, result }` or `{ ok: false, error: { code, message, details? } }`,
 * with the HTTP status from {@link ERROR_HTTP_STATUS}. Events stream over a WebSocket at {@link EVENTS_WS_PATH}.
 *
 * Swapping the mock for the real coordinator is then a config change: point `baseUrl` somewhere else.
 */
import {
  COORDINATOR_OPERATIONS,
  CoordinatorError,
  ERROR_HTTP_STATUS,
  EVENTS_TICKET_PATH,
  EVENTS_WS_PATH,
  SESSION_TOKEN_HEADER,
  RpcResponseSchema,
  ServerFrameSchema,
  coordinatorOperations,
  isCoordinatorError,
  rpcPath,
  type ClientFrame,
  type Coordinator,
  type CoordinatorOperation,
  type RpcResponse,
  type SubscribeOptions,
  type Subscription,
} from "./coordinator.ts";
import type { PlatformEvent } from "./events.ts";

const RPC_PREFIX = "/v1/rpc/";

function json(body: RpcResponse, status: number): Response {
  return new Response(JSON.stringify(body), { status, headers: { "content-type": "application/json" } });
}

function errorResponse(e: unknown): Response {
  if (isCoordinatorError(e)) return json({ ok: false, error: e.toJSON() }, ERROR_HTTP_STATUS[e.code]);
  const message = e instanceof Error ? e.message : String(e);
  return json({ ok: false, error: { code: "internal", message } }, 500);
}

/**
 * Serve a coordinator's request/response operations over HTTP. Returns null for paths it does not own,
 * so you can chain it with other routes (for example the WebSocket upgrade at {@link EVENTS_WS_PATH}).
 */
export function createRpcHandler(coordinator: Coordinator): (request: Request) => Promise<Response | null> {
  return async (request) => {
    const url = new URL(request.url);
    if (!url.pathname.startsWith(RPC_PREFIX)) return null;
    const operation = url.pathname.slice(RPC_PREFIX.length) as CoordinatorOperation;
    if (!(operation in coordinatorOperations)) {
      return json({ ok: false, error: { code: "not_found", message: `unknown operation ${operation}` } }, 404);
    }
    if (request.method !== "POST") {
      return json({ ok: false, error: { code: "invalid_input", message: "use POST" } }, 405);
    }
    let body: unknown;
    try {
      const text = await request.text();
      body = text.length === 0 ? {} : JSON.parse(text);
    } catch {
      return json({ ok: false, error: { code: "invalid_input", message: "body is not valid JSON" } }, 400);
    }
    try {
      const method = coordinator[operation] as (input: unknown) => Promise<unknown>;
      const result = await method.call(coordinator, body);
      return json({ ok: true, result }, 200);
    } catch (e) {
      return errorResponse(e);
    }
  };
}

/** Minimal WebSocket shape the client needs (the global WebSocket in Workers, browsers and Node 22 fits). */
export interface WebSocketLike {
  send(data: string): void;
  close(): void;
  addEventListener(type: "open" | "close" | "error", listener: () => void): void;
  addEventListener(type: "message", listener: (event: { data: unknown }) => void): void;
}

/** Options for {@link createHttpCoordinator}. */
export interface HttpCoordinatorOptions {
  /** Base URL of the coordinator, for example `https://coordinator.botallion.dev`. */
  baseUrl: string;
  /** Extra headers on every RPC request, for example an authorization token. */
  headers?: Record<string, string>;
  /** fetch implementation. Defaults to the global fetch. */
  fetch?: (input: string, init: RequestInit) => Promise<Response>;
  /** WebSocket constructor. Defaults to the global WebSocket. */
  WebSocket?: new (url: string) => WebSocketLike;
  /** Delay before reconnecting a dropped event stream. Default 1000 ms. Set to -1 to disable reconnecting. */
  reconnectDelayMs?: number;
  /**
   * Bearer token for one call, chosen by operation and input (for example the acting session's own token).
   * Overrides any `authorization` in `headers`. See "Auth" in the README.
   */
  tokenFor?: (operation: CoordinatorOperation, input: unknown) => string | undefined;
  /** Called with each new session's token, read from `registerSession`'s {@link SESSION_TOKEN_HEADER}. */
  onSessionToken?: (sessionId: string, token: string) => void;
  /**
   * Open the event WebSocket with a single-use ticket (`POST` {@link EVENTS_TICKET_PATH} using `headers`, then
   * `?ticket=`), fetched again on every reconnect. Use it wherever a WebSocket cannot send headers (browsers, Node).
   */
  eventTickets?: boolean;
}

/**
 * A {@link Coordinator} backed by HTTP RPC and the event WebSocket. Outputs are validated against
 * their schemas, so a contract mismatch fails loudly at the call site.
 */
export function createHttpCoordinator(options: HttpCoordinatorOptions): Coordinator {
  const base = options.baseUrl.replace(/\/+$/, "");
  const doFetch = options.fetch ?? ((input: string, init: RequestInit) => fetch(input, init));

  const call = async (operation: CoordinatorOperation, input: unknown): Promise<unknown> => {
    const token = options.tokenFor?.(operation, input ?? {});
    const res = await doFetch(base + rpcPath(operation), {
      method: "POST",
      headers: { "content-type": "application/json", ...options.headers, ...(token ? { authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(input ?? {}),
    });
    let parsed: RpcResponse;
    try {
      parsed = RpcResponseSchema.parse(await res.json());
    } catch {
      throw new CoordinatorError("internal", `unexpected response from ${operation} (HTTP ${res.status})`);
    }
    if (!parsed.ok) throw new CoordinatorError(parsed.error.code, parsed.error.message, parsed.error.details);
    const result = coordinatorOperations[operation].output.parse(parsed.result);
    const issued = res.headers.get(SESSION_TOKEN_HEADER);
    if (operation === "registerSession" && issued) options.onSessionToken?.((result as { id: string }).id, issued);
    return result;
  };

  const subscribe = (opts: SubscribeOptions, listener: (event: PlatformEvent) => void): Subscription => {
    const Ctor = options.WebSocket ?? (globalThis as unknown as { WebSocket: new (url: string) => WebSocketLike }).WebSocket;
    const wsUrl = base.replace(/^http/, "ws") + EVENTS_WS_PATH;
    const delay = options.reconnectDelayMs ?? 1000;
    let closed = false;
    let lastSeq = opts.since;
    let socket: WebSocketLike | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const ticket = async (): Promise<string> => {
      const res = await doFetch(base + EVENTS_TICKET_PATH, { method: "POST", headers: { ...options.headers } });
      const body = RpcResponseSchema.parse(await res.json());
      if (!body.ok) throw new CoordinatorError(body.error.code, body.error.message);
      return (body.result as { ticket: string }).ticket;
    };

    const connect = () => {
      if (!options.eventTickets) return open(wsUrl);
      ticket().then(
        (t) => {
          if (!closed) open(`${wsUrl}?ticket=${encodeURIComponent(t)}`);
        },
        () => {
          if (!closed && delay >= 0) timer = setTimeout(connect, delay);
        },
      );
    };

    const open = (url: string) => {
      const ws = new Ctor(url);
      socket = ws;
      ws.addEventListener("open", () => {
        const frame: ClientFrame = { type: "subscribe" };
        if (lastSeq !== undefined) frame.since = lastSeq;
        if (opts.types) frame.types = opts.types;
        ws.send(JSON.stringify(frame));
      });
      ws.addEventListener("message", (msg) => {
        const frame = ServerFrameSchema.safeParse(JSON.parse(String(msg.data)));
        if (!frame.success || frame.data.type !== "event") return;
        // Skip anything already delivered (possible after a reconnect).
        if (lastSeq !== undefined && frame.data.event.seq <= lastSeq) return;
        lastSeq = frame.data.event.seq;
        listener(frame.data.event);
      });
      ws.addEventListener("close", () => {
        // After a drop, resume from the last seq seen; with no events yet, resume live from now.
        if (!closed && delay >= 0) timer = setTimeout(connect, delay);
      });
    };
    connect();
    return {
      close() {
        closed = true;
        if (timer !== undefined) clearTimeout(timer);
        socket?.close();
      },
    };
  };

  const client: Record<string, unknown> = { subscribe };
  for (const op of COORDINATOR_OPERATIONS) client[op] = (input?: unknown) => call(op, input);
  return client as unknown as Coordinator;
}
